import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Avatar,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Controller, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import StandInsights from "../Components/StandInsights";
import { GET_MY_AUTH } from "../graphql/queries";
import EnhancedTable from "../Components/EnhancedTable";

export default function AddStaffPage() {
  const { data, error, loading } = useQuery(GET_MY_AUTH);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const eventId = data && data?.eventId;
  const standId = data && data?.standId;
  const company = data && data?.company;
  const allowed = data && data?.allowed;

  /*
   * ADMIN ONLY — ENFORCED HERE, NOT JUST IN THE MENU.
   *
   * The nav entry is admin-only, but a menu is decoration: /staff is a plain route and an
   * attendee who has ever seen the URL can open it. Scanners are the accounts that read badges,
   * so being able to mint one is not a thing to leave behind a hidden link.
   *
   * The server is still the real boundary — it checks the token on every mutation. This stops
   * the page being USEFUL to the wrong person, and stops it being confusing: a scanner opening
   * it would otherwise see "Staff Members" and an empty table, and reasonably conclude the
   * feature was broken.
   */
  const isAdmin = data?.me === "admin";

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <LinearProgress sx={{ width: "50%" }} />
      </Box>
    );

  if (error)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading staff management: {error.message}
      </Alert>
    );

  /* Checked AFTER loading and error, so a slow auth query does not flash "not available". */
  if (!isAdmin)
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        Only an event administrator can add or manage staff accounts.
      </Alert>
    );


  return (
    <Box sx={{ p: 0 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontSize: { xs: "1.5rem", md: "2rem" },
          fontWeight: 600,
          color: "primary.main",
          mb: 3,
        }}
      >
        Manage Staff
      </Typography>

      <StandsTable
        eventId={eventId}
        standId={standId}
        company={company}
        allowed={allowed}
        onNotify={setNotification}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function StandsTable({ eventId, standId, company, allowed, onNotify }) {
  const { data, loading, refetch } = useQuery(GET_STANDS, {
    variables: { eventId },
    fetchPolicy: "network-only",
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentStaff, setCurrentStaff] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [staffToDelete, setStaffToDelete] = useState([]);

  const [updateStand] = useMutation(UPDATE_STAND);
  const [deleteStands] = useMutation(DELETE_STANDS);

  // Add this constant to display the staff limit
  const STAFF_LIMIT = allowed;
  const currentStaffCount = data?.stands?.length || 0;
  const reachedLimit = currentStaffCount >= STAFF_LIMIT;

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 100,
      align: "center",
    },
    {
      field: "name",
      headerName: "Name",
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Avatar
            sx={{
              width: 30,
              height: 30,
              mr: 1,
              bgcolor: row.name ? stringToColor(row.name) : "primary.main",
            }}
          >
            {row.name?.charAt(0) || "U"}
          </Avatar>
          <Typography variant="body2">{row.name || "Unknown"}</Typography>
        </Box>
      ),
    },
    {
      field: "company",
      headerName: "Company",
    },
    {
      field: "email",
      headerName: "Email/UserID",
    },
    {
      field: "password",
      headerName: "Password",
      renderCell: ({ row }) => (
        <Typography variant="body2">{hidePassword(row.password)}</Typography>
      ),
    },
    // {
    //   field: "created",
    //   headerName: "Created On",
    //   renderCell: ({ row }) => (
    //     <Typography variant="body2">{formatDate(row.created)}</Typography>
    //   ),
    // },
  ];

  const handleEditStaff = (staff) => {
    setCurrentStaff(staff);
    setEditDialogOpen(true);
  };

  const handleDeleteStaff = (staff) => {
    setStaffToDelete([staff.id]);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteStands({
        variables: {
          eventId,
          ids: staffToDelete,
        },
      });

      refetch();
      onNotify({
        open: true,
        message: `Successfully deleted ${staffToDelete.length} staff member(s)`,
        severity: "success",
      });
      setDeleteDialogOpen(false);
      setStaffToDelete([]);
    } catch (error) {
      onNotify({
        open: true,
        message: `Failed to delete: ${error.message}`,
        severity: "error",
      });
    }
  };

  // Helper functions
  const hidePassword = (password) => {
    if (!password) return "";
    return "•".repeat(password.length);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return (
        date.toLocaleDateString() +
        " " +
        date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
    } catch (e) {
      return dateString;
    }
  };

  const stringToColor = (string) => {
    if (!string) return "#2e7d32";
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = "#";
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
  };

  // Update the customActions to disable the Add Staff button when limit reached
  const customActions = (
    <>
      <Button
        variant="contained"
        startIcon={<RefreshIcon />}
        onClick={() => refetch()}
        size="small"
      >
        Refresh
      </Button>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={() => {
          if (reachedLimit) {
            onNotify({
              open: true,
              message: `Staff limit reached (${currentStaffCount}/${STAFF_LIMIT}). You cannot add more staff members.`,
              severity: "warning",
            });
            return;
          }
          setCurrentStaff(null);
          setEditDialogOpen(true);
        }}
        size="small"
        disabled={reachedLimit}
      >
        Add Staff
      </Button>
      {/* Add a staff count indicator */}
      <Chip
        label={`${currentStaffCount}/${STAFF_LIMIT} staff`}
        color={reachedLimit ? "error" : "default"}
        size="small"
        sx={{ ml: 1 }}
      />
    </>
  );

  return (
    <>
      <EnhancedTable
        title="Staff Members"
        columns={columns}
        data={data?.stands || []}
        loading={loading}
        onEdit={handleEditStaff}
        onDelete={handleDeleteStaff}
        customActions={customActions}
        filters={[
          {
            name: "Company",
            field: "company",
            options: data?.stands
              ? Array.from(new Set(data.stands.map((s) => s.company)))
                  .filter(Boolean)
                  .map((c) => ({ label: c, value: c }))
              : [],
          },
        ]}
      />

      <AddEditStaffDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        staff={currentStaff}
        eventId={eventId}
        standId={standId}
        company={company}
        onSuccess={() => {
          refetch();
          setEditDialogOpen(false);
          onNotify({
            open: true,
            message: `Staff ${currentStaff ? "updated" : "added"} successfully`,
            severity: "success",
          });
        }}
        onError={(error) => {
          onNotify({
            open: true,
            message: `Error: ${error.message}`,
            severity: "error",
          });
        }}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {staffToDelete.length} staff
            member(s)? This action cannot be undone.
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            sx={{ mt: 3, justifyContent: "flex-end" }}
          >
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleConfirmDelete}
            >
              Delete
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AddEditStaffDialog({
  open,
  onClose,
  staff,
  eventId,
  standId,
  company,
  onSuccess,
  onError,
}) {
  const [addStand, { loading: addLoading, error: addError }] = useMutation(
    ADD_STAND,
    {
      onError: (error) => {
        // This will capture network errors and GraphQL errors
        onError(error);
      },
    }
  );

  const [updateStand, { loading: updateLoading, error: updateError }] =
    useMutation(UPDATE_STAND, {
      onError: (error) => {
        onError(error);
      },
    });

  const isEdit = Boolean(staff);

  const { control, formState, handleSubmit, reset } = useForm({
    defaultValues: {
      company: staff?.company || company || "",
      email: staff?.email || "",
      password: staff?.password || "",
      name: staff?.name || "",
      parentId: staff?.parentId || standId || "",
    },
  });

  React.useEffect(() => {
    if (open) {
      reset({
        company: staff?.company || company || "",
        email: staff?.email || "",
        password: staff?.password || "",
        name: staff?.name || "",
        parentId: staff?.parentId || standId || "",
      });
    }
  }, [open, staff, company, standId, reset]);

  const onSubmit = async (formData) => {
    try {
      let response;

      if (isEdit) {
        // Update existing staff
        const updateResults = await Promise.all([
          updateStand({
            variables: {
              eventId,
              id: staff.id,
              field: "name",
              value: formData.name,
            },
          }),
          updateStand({
            variables: {
              eventId,
              id: staff.id,
              field: "email",
              value: formData.email,
            },
          }),
          updateStand({
            variables: {
              eventId,
              id: staff.id,
              field: "password",
              value: formData.password,
            },
          }),
          updateStand({
            variables: {
              eventId,
              id: staff.id,
              field: "company",
              value: formData.company,
            },
          }),
        ]);

        // Check if any update operations failed
        for (const result of updateResults) {
          if (result.errors) {
            throw new Error(result.errors[0]?.message || "Update failed");
          }
        }

        response = updateResults[0]; // Use the first response for success
      } else {
        // Add new staff
        response = await addStand({
          variables: {
            eventId,
            inp: formData,
          },
        });

        // Additional check for errors in the response data
        if (
          response.errors ||
          (response.data && response.data.result === null)
        ) {
          const errorMessage =
            response.errors?.[0]?.message ||
            "Failed to add staff member. Please check your limits.";
          throw new Error(errorMessage);
        }
      }

      onSuccess();
    } catch (error) {
      console.error("Staff operation error:", error);
      onError(error);
    }
  };

  // Display any Apollo error messages directly in the dialog
  const apolloError = addError || updateError;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Update Staff Member" : "Add New Staff Member"}
      </DialogTitle>
      <DialogContent>
        {apolloError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {apolloError.message}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Controller
              control={control}
              name="name"
              rules={{
                required: "Name is required",
                minLength: {
                  value: 2,
                  message: "Name must be at least 2 characters",
                },
              }}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  inputRef={ref}
                  {...field}
                  error={!!error}
                  helperText={error?.message}
                  label="Name"
                  required
                  fullWidth
                  autoFocus
                />
              )}
            />

            <Controller
              control={control}
              name="email"
              rules={{
                required: "Email/UserID is required",
                minLength: {
                  value: 3,
                  message: "Email must be at least 3 characters",
                },
              }}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  inputRef={ref}
                  {...field}
                  error={!!error}
                  helperText={error?.message}
                  label="Email/UserID"
                  required
                  fullWidth
                />
              )}
            />

            <Controller
              control={control}
              name="password"
              rules={{
                required: "Password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters",
                },
              }}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  inputRef={ref}
                  {...field}
                  error={!!error}
                  helperText={error?.message}
                  label="Password"
                  type="password"
                  required
                  fullWidth
                />
              )}
            />

            <Controller
              control={control}
              name="company"
              rules={{ required: "Company name is required" }}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <TextField
                  inputRef={ref}
                  {...field}
                  error={!!error}
                  helperText={error?.message}
                  label="Company"
                  required
                  fullWidth
                />
              )}
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={onClose}>Cancel</Button>
              <LoadingButton
                loading={formState.isSubmitting}
                variant="contained"
                type="submit"
                color="primary"
              >
                {isEdit ? "Update" : "Create"}
              </LoadingButton>
            </Stack>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const GET_STANDS = gql`
  query ($eventId: String!) {
    stands(eventId: $eventId) {
      id
      email
      company
      password
      created
      name
      parentId
    }
  }
`;

const ADD_STAND = gql`
  mutation ($eventId: String!, $inp: StandInput!) {
    result: createStand(eventId: $eventId, inp: $inp)
  }
`;
// const UPLOAD_STANDS = gql`
//   mutation ($eventId: String!, $stands: [StandInput]) {
//     result: uploadStands(eventId: $eventId, stands: $stands)
//   }
// `;
const UPDATE_STAND = gql`
  mutation ($eventId: String!, $id: String!, $field: String!, $value: String!) {
    result: updateStand(
      eventId: $eventId
      id: $id
      field: $field
      value: $value
    )
  }
`;

const DELETE_STANDS = gql`
  mutation ($eventId: String!, $ids: [String]!) {
    result: deleteStands(eventId: $eventId, ids: $ids)
  }
`;

// const ADD_ALLOWED_COLUMN = gql`
// mutation ($eventId: String!, $value: String!) {
//   addAllowedColumn(eventId: $eventId, value: $value)
// }
// `;

export const GET_FORM_FIELDS = gql`
  query ($eventId: String!) {
    getAllFormField(eventId: $eventId)
  }
`;
export const GET_SELECTED_FORM_FIELDS = gql`
  query ($eventId: String!) {
    getSelectedFormField(eventId: $eventId)
  }
`;
